import { useEffect, useRef } from "react"

interface UseGlobalObjectCheckerOptions {
  globalKey: string
  onReady: () => void
  enabled?: boolean
}

/**
 * 全局对象检查器
 * 用于等待全局对象加载完成后执行回调
 * 配置：300ms 间隔，最多重试 5 次
 */
const useGlobalObjectChecker = ({
  globalKey,
  onReady,
  enabled = true,
}: UseGlobalObjectCheckerOptions) => {
  const attemptCountRef = useRef(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const hasExecutedRef = useRef(false)

  // 写死的配置
  const INTERVAL = 300 // 300ms 检查间隔
  const MAX_ATTEMPTS = 5 // 最多 5 次尝试

  useEffect(() => {
    if (!enabled || hasExecutedRef.current) {
      return
    }

    const checkGlobalObject = () => {
      attemptCountRef.current += 1

      // 检查全局对象是否存在
      const globalObject = (window as any)[globalKey]
      
      if (globalObject) {
        // 全局对象存在，执行回调
        onReady()
        hasExecutedRef.current = true
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
        return
      }

      // 检查是否超过最大尝试次数
      if (attemptCountRef.current >= MAX_ATTEMPTS) {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
        return
      }

      // 继续下一次检查
      timerRef.current = setTimeout(checkGlobalObject, INTERVAL)
    }

    // 开始检查
    timerRef.current = setTimeout(checkGlobalObject, INTERVAL)

    // 清理函数
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [globalKey, onReady, enabled])

  // 重置函数
  const reset = () => {
    hasExecutedRef.current = false
    attemptCountRef.current = 0
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  return { reset }
}

export default useGlobalObjectChecker
